import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, targetAPI, crmAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { useGlobalFilters } from '../../lib/GlobalFilterContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CompactBusinessRules } from '../layout/CompactBusinessRules';
import { DataFreshnessBar } from '../layout/DataFreshnessBar';
import { RoleScopeControl } from '../layout/RoleScopeControl';
import { 
  TrendingUp, 
  TrendingDown,
  Users, 
  Building2, 
  DollarSign, 
  Target,
  BarChart3,
  PieChart,
  Activity,
  Brain,
  RefreshCw,
  Filter,
  Sparkles,
  ArrowRight,
  Award,
  AlertTriangle,
  CheckCircle, CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  ChevronRight,
  Trophy,
  ShoppingCart,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [displayMode, setDisplayMode] = useState('compact');
  const formatCurrencyCompact = (value) => {
    const num = Number(value || 0);
    if (Math.abs(num) >= 1000000) return `OMR ${(num / 1000000).toFixed(2)}M`;
    if (Math.abs(num) >= 1000) return `OMR ${(num / 1000).toFixed(1)}K`;
    return formatCurrency(num);
  };
  const money = (value) => displayMode === 'compact' ? formatCurrencyCompact(value) : formatCurrency(value || 0);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [repPerformance, setRepPerformance] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState(null);
  const [accountHealth, setAccountHealth] = useState(null);
  const [invoiceHealth, setInvoiceHealth] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [localFilters, setLocalFilters] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [viewMode] = useState('ai');
  const [dashBlocks] = useState([]);
  const [dashLoading] = useState(false);
  
  // Modal state for At Risk/Dormant accounts
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [riskModalType, setRiskModalType] = useState('at_risk'); // 'healthy', 'at_risk', 'dormant'
  
  // Use global filters
  const { filters: globalFilters, hasActiveFilters } = useGlobalFilters();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Build params from local filters (which take precedence on this page)
      const params = {};
      
      // Use local year/quarter selection if set, otherwise use global
      if (selectedYear) {
        params.year = selectedYear;
      } else if (globalFilters.year) {
        params.year = globalFilters.year;
      }
      
      if (selectedQuarter) {
        params.quarter = selectedQuarter;
      } else if (globalFilters.quarter) {
        params.quarter = globalFilters.quarter;
      }
      
      // Add other global filters
      if (globalFilters.salesRep) params.sales_rep = globalFilters.salesRep;
      if (globalFilters.account) params.account = globalFilters.account;
      
      // Add time_period for backward compatibility
      if (selectedPeriod && selectedPeriod !== 'all') {
        params.time_period = selectedPeriod;
      }
      
      const [overviewRes, funnelRes, repRes, teamRes, accountRes, filtersRes, soStatsRes, invStatsRes, invoiceHealthRes] = await Promise.all([
        analyticsAPI.getOverview(params),
        analyticsAPI.getConversionFunnel(params),
        analyticsAPI.getRepPerformance(params),
        analyticsAPI.getTeamPerformance(params),
        analyticsAPI.getAccountHealth(params),
        analyticsAPI.getFilters(),
        crmAPI.getSalesOrdersStats({ ...(params.year ? { year: params.year } : {}), ...(params.quarter ? { quarter: params.quarter } : {}), ...(params.sales_rep ? { salesperson: params.sales_rep } : {}), ...(params.account ? { customer: params.account } : {}) }),
        crmAPI.getReceivablesStats({ ...(params.year ? { year: params.year } : {}), ...(params.quarter ? { quarter: params.quarter } : {}), ...(params.sales_rep ? { salesperson: params.sales_rep } : {}), ...(params.account ? { account: params.account } : {}) }),
        crmAPI.getInvoiceCollectionHealth({ ...(params.year ? { year: params.year } : {}), ...(params.quarter ? { quarter: params.quarter } : {}), ...(params.sales_rep ? { salesperson: params.sales_rep } : {}), ...(params.account ? { account: params.account } : {}) }),
      ]);
      
      setOverview({ ...overviewRes.data, soStats: soStatsRes.data, invStats: invStatsRes.data?.stats });
      setFunnel(funnelRes.data);
      setRepPerformance(repRes.data);
      setTeamPerformance(teamRes.data);
      setAccountHealth(accountRes.data);
      setInvoiceHealth(invoiceHealthRes.data);
      setLocalFilters(filtersRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, selectedYear, selectedQuarter, globalFilters.year, globalFilters.quarter, globalFilters.salesRep, globalFilters.account]);

  const loadAIInsights = async () => {
    setAiLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    try {
      const params = {};
      if (selectedYear) params.year = selectedYear;
      if (selectedQuarter) params.quarter = selectedQuarter;
      if (globalFilters.salesRep) params.sales_rep = globalFilters.salesRep;
      if (globalFilters.account) params.account = globalFilters.account;
      const response = await analyticsAPI.getAIInsights(params, { signal: controller.signal });
      clearTimeout(timeoutId);
      setAiInsights(response.data);
      toast.success('AI insights generated');
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Failed to generate AI insights:', error);
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        toast.error('AI insights generation timed out. Please try again.');
      } else if (error.response?.status === 500) {
        toast.error('AI service error. Please try again later.');
      } else {
        toast.error('Failed to generate AI insights');
      }
    } finally {
      setAiLoading(false);
    }
  };

  // Insights only generated on explicit user trigger (not auto-loaded)
  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
          <div className="flex gap-2"><Skeleton className="h-9 w-32" /><Skeleton className="h-9 w-28" /></div>
        </div>
        {/* 3-row skeleton matching the 9-KPI layout */}
        <div className="space-y-3">
          <Skeleton className="h-4 w-36" />
          <div className="grid grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={`sk-a-${i}`} className="h-28 rounded-2xl" />)}</div>
          <Skeleton className="h-4 w-28 mt-2" />
          <div className="grid grid-cols-3 gap-3">{[...Array(3)].map((_, i) => <Skeleton key={`sk-b-${i}`} className="h-28 rounded-2xl" />)}</div>
          <Skeleton className="h-4 w-24 mt-2" />
          <div className="grid grid-cols-3 gap-3">{[...Array(3)].map((_, i) => <Skeleton key={`sk-c-${i}`} className="h-28 rounded-2xl" />)}</div>
        </div>
        <Skeleton className="h-12 w-full rounded-full" />
        <Skeleton className="h-[380px] rounded-2xl" />
      </div>
    );
  }

  // Gradient styles using inline (not Tailwind — JIT purges dynamic classes)
  const CARD_GRADIENTS = [
    { style: { background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)' }, shadow: 'rgba(5,150,105,0.35)' },
    { style: { background: 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)' }, shadow: 'rgba(59,130,246,0.35)' },
    { style: { background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }, shadow: 'rgba(139,92,246,0.35)' },
    { style: { background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)' }, shadow: 'rgba(245,158,11,0.35)' },
  ];


  // ── 9-KPI Cards: Full sales-to-cash journey ──────────────────────────────
  const so = overview?.soStats || {};
  const inv = overview?.invStats || {};

  const KPI_GROUPS = [
    {
      label: 'PIPELINE & DEALS',
      shell: 'from-[#eff6ff] via-white to-[#eef2ff]',
      cards: [
        { label: 'Total Pipeline', value: money(overview?.summary?.total_pipeline || 0), sub: `${overview?.summary?.total_opportunities || 0} open opps`, icon: DollarSign, accent: '#2563eb', glow: 'rgba(37,99,235,0.18)', tint: 'rgba(37,99,235,0.08)' },
        { label: 'Won', value: money(overview?.summary?.won_value || 0), sub: `${overview?.summary?.won_count || 0} deals`, icon: Award, accent: '#059669', glow: 'rgba(5,150,105,0.18)', tint: 'rgba(16,185,129,0.09)' },
        { label: 'Win Rate', value: `${overview?.summary?.win_rate || 0}%`, sub: `Avg: ${formatCurrency(overview?.summary?.avg_deal_size || 0)}`, icon: Target, accent: '#7c3aed', glow: 'rgba(124,58,237,0.18)', tint: 'rgba(139,92,246,0.09)' },
        { label: 'Lost', value: money(overview?.summary?.lost_value || 0), sub: `${overview?.summary?.lost_count || 0} lost`, icon: XCircle, accent: '#dc2626', glow: 'rgba(220,38,38,0.16)', tint: 'rgba(248,113,113,0.08)' },
      ],
    },
    {
      label: 'SALES ORDERS',
      shell: 'from-[#ecfeff] via-white to-[#eff6ff]',
      cards: [
        { label: 'Total SOs', value: so.total_orders || 0, sub: money(so.total_value || 0), icon: ShoppingCart, accent: '#0f766e', glow: 'rgba(15,118,110,0.18)', tint: 'rgba(45,212,191,0.08)' },
        { label: 'Fully Invoiced', value: so.fully_invoiced || 0, sub: money(so.by_status?.fully_invoiced?.amount || 0), icon: CheckCircle, accent: '#059669', glow: 'rgba(5,150,105,0.18)', tint: 'rgba(16,185,129,0.09)' },
        { label: 'Partially Invoiced', value: so.partially_invoiced || 0, sub: money(so.by_status?.partially_invoiced?.amount || 0), icon: Clock, accent: '#d97706', glow: 'rgba(217,119,6,0.18)', tint: 'rgba(251,191,36,0.11)' },
        { label: 'Not Invoiced', value: so.not_invoiced || 0, sub: money(so.by_status?.not_invoiced?.amount || 0), icon: AlertTriangle, accent: '#475569', glow: 'rgba(71,85,105,0.16)', tint: 'rgba(148,163,184,0.10)' },
      ],
    },
    {
      label: 'COLLECTION',
      shell: 'from-[#fdf4ff] via-white to-[#faf5ff]',
      cards: [
        { label: 'Invoiced', value: money(inv.total_invoiced || 0), sub: `${inv.count_total || 0} invoices`, icon: FileText, accent: '#6d28d9', glow: 'rgba(109,40,217,0.18)', tint: 'rgba(168,85,247,0.09)' },
        { label: 'Collected', value: money(inv.total_paid || 0), sub: `${inv.count_paid || 0} paid`, icon: CheckCircle, accent: '#047857', glow: 'rgba(4,120,87,0.18)', tint: 'rgba(16,185,129,0.09)' },
        { label: 'Overdue', value: money(inv.total_overdue || 0), sub: `${inv.count_overdue || 0} invoices`, icon: AlertTriangle, accent: '#dc2626', glow: 'rgba(220,38,38,0.16)', tint: 'rgba(248,113,113,0.08)' },
      ],
    },
  ];

  return (
    <div className="space-y-5 p-6 bg-[radial-gradient(circle_at_top_left,_rgba(128,0,0,0.04),_transparent_35%),radial-gradient(circle_at_top_right,_rgba(37,99,235,0.05),_transparent_28%),linear-gradient(180deg,_#fcfcfd_0%,_#f8fafc_100%)]" data-testid="analytics-page">
      {/* Header — hero strip with filters inline */}
      <div className="rounded-[28px] border border-slate-200 bg-white/90 backdrop-blur shadow-sm px-5 py-5 flex items-center justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#800000]/8 px-3 py-1 text-[11px] font-semibold text-[#800000] mb-3">
            <Sparkles className="h-3.5 w-3.5" /> Executive AI Revenue View
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 text-slate-900">
            <div className="p-2 rounded-2xl bg-gradient-to-br from-[#800000]/12 to-[#1d4ed8]/10 border border-slate-100 shadow-sm">
              <Brain className="h-5 w-5 text-[#800000]" />
            </div>
            AI Sales Analytics
          </h1>
          <p className="text-slate-500 text-sm mt-2 leading-relaxed">Decision-ready view of pipeline, order-to-cash flow, and collections performance with AI-generated interpretation.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <RoleScopeControl dataTestId="analytics-role-scope-control" />
          <CompactBusinessRules
            scopeItems={[selectedYear, selectedQuarter, globalFilters.salesRep, globalFilters.account].filter(Boolean)}
            ruleKeys={['open_pipeline', 'won_lost_hold', 'invoice_progress', 'collection_status', 'totals_scope']}
            label="Rules"
            dataTestId="analytics-compact-business-rules"
          />
          <Select value={displayMode} onValueChange={setDisplayMode}>
            <SelectTrigger className="w-32 h-9 rounded-full border-muted" data-testid="analytics-display-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="compact">Compact</SelectItem>
              <SelectItem value="exact">Exact</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedYear || 'all'} onValueChange={(v) => setSelectedYear(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-28 h-9 rounded-full border-muted" data-testid="analytics-year-filter">
              <SelectValue placeholder="All Years" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {localFilters?.years?.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedQuarter || 'all'} onValueChange={(v) => setSelectedQuarter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-28 h-9 rounded-full border-muted" data-testid="analytics-quarter-filter">
              <SelectValue placeholder="All Qtrs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Quarters</SelectItem>
              {['Q1','Q2','Q3','Q4'].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
            </SelectContent>
          </Select>
          {(selectedYear || selectedQuarter) && (
            <button
              className="flex items-center gap-1.5 h-9 px-3 rounded-full bg-muted text-sm font-medium hover:bg-destructive/10 transition-colors"
              onClick={() => { setSelectedYear(''); setSelectedQuarter(''); }}
            >
              {selectedYear || 'All'} {selectedQuarter || ''}
              <span className="text-muted-foreground">×</span>
            </button>
          )}
        </div>
      </div>
      <DataFreshnessBar entityIds={['opportunities', 'sales_orders', 'invoices']} actionEntityIds={['opportunities', 'sales_orders', 'invoices']} title="Analytics Data Freshness" dataTestId="analytics-data-freshness" />

      {/* KPI Cards — 3 groups: Pipeline & Deals, Sales Orders, Collection */}
      <div className="space-y-3">
        {KPI_GROUPS.map((group, gi) => (
          <div key={group.label} className={`rounded-2xl border border-slate-200/80 bg-gradient-to-br ${group.shell} shadow-sm p-3 md:p-4`}>
            {/* Group label */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold tracking-[0.12em] text-slate-500">{group.label}</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div className={`grid gap-3 ${group.cards.length === 4 ? 'grid-cols-2 lg:grid-cols-4' : group.cards.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2'}`}>
              {group.cards.map((kpi) => {
                const Icon = kpi.icon;
                return (
                  <div key={kpi.label}
                    className="relative overflow-hidden rounded-2xl border border-white/70 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg min-h-[148px]"
                    style={{ boxShadow: `0 10px 26px ${kpi.glow}` }}
                    data-testid={`kpi-card-${kpi.label.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: `linear-gradient(90deg, ${kpi.accent} 0%, ${kpi.accent}99 60%, transparent 100%)` }} />
                    <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl" style={{ backgroundColor: kpi.tint }} />
                    <div className="relative z-10 pl-2 h-full flex flex-col justify-between">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <p className="text-slate-500 text-[10px] font-bold uppercase tracking-[0.12em] leading-none">{kpi.label}</p>
                        <div className="p-2 rounded-2xl border border-slate-100 shrink-0" style={{ backgroundColor: kpi.tint }}>
                          <Icon className="h-4 w-4" style={{ color: kpi.accent }} />
                        </div>
                      </div>
                      <p className="text-[clamp(1.75rem,2.1vw,2.8rem)] font-bold tracking-tight leading-tight text-slate-900 break-words">{kpi.value}</p>
                      <p className="text-slate-500 text-[11px] mt-2 font-medium leading-snug">{kpi.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Tabs — pill style */}
      <Tabs defaultValue="funnel" className="space-y-5">
        <div className="flex items-center justify-center">
          <TabsList className="inline-flex h-10 rounded-full p-1 bg-muted gap-0.5">
            <TabsTrigger value="funnel" className="rounded-full px-4 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" />Conversion Funnel
            </TabsTrigger>
            <TabsTrigger value="reps" className="rounded-full px-4 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />Rep Performance
            </TabsTrigger>
            <TabsTrigger value="teams" className="rounded-full px-4 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5" />PM & Categories
            </TabsTrigger>
            <TabsTrigger value="accounts" className="rounded-full px-4 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5" />Account Health
            </TabsTrigger>
            <TabsTrigger value="ai" className="rounded-full px-4 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />AI Insights
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Conversion Funnel Tab */}
        <TabsContent value="funnel" className="space-y-4">
          <Card className="border-0 shadow-sm rounded-2xl overflow-hidden">
            <CardHeader className="pb-2 bg-gradient-to-r from-muted/30 to-transparent">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PieChart className="h-5 w-5 text-[#800000]" />
                    Sales Conversion Funnel
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    Win rate: <span className="font-bold text-[#800000] text-base">{funnel?.overall_conversion || 0}%</span>
                    {' · '}
                    <span className="font-medium">{funnel?.funnel?.find(s=>s.stage==='Won')?.count || 0} won</span>
                    {' / '}
                    <span>{(funnel?.funnel?.find(s=>s.stage==='Won')?.count||0) + (funnel?.lost_count||0)} closed</span>
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {funnel?.funnel?.length > 0 ? (
                <div className="space-y-3 py-2">
                  {funnel.funnel.map((stage, i) => {
                    const maxCount = Math.max(...funnel.funnel.map(s => s.count), 1);
                    const pct = stage.count === 0 ? 16 : Math.max(22, (stage.count / maxCount) * 100);
                    const STAGE_STYLES = [
                      { from: '#94a3b8', to: '#64748b' },
                      { from: '#3b82f6', to: '#4f46e5' },
                      { from: '#14b8a6', to: '#0891b2' },
                      { from: '#8b5cf6', to: '#7c3aed' },
                      { from: '#10b981', to: '#059669' },
                    ];
                    const s = STAGE_STYLES[i % STAGE_STYLES.length];
                    const isEmpty = stage.count === 0;
                    return (
                      <div key={stage.stage} className="flex items-center gap-0">
                        {/* Stage label — fixed width, right-aligned */}
                        <span className="text-sm font-semibold text-slate-600 w-28 text-right pr-4 shrink-0">{stage.stage}</span>

                        {/* Bar container — flex-1 for full width reference */}
                        <div className="flex-1 flex items-center gap-3">
                          {/* The funnel bar */}
                          <div
                            className="relative h-14 flex items-center justify-center rounded-xl transition-all duration-500 cursor-default overflow-hidden"
                            style={{
                              width: `${pct}%`,
                              minWidth: isEmpty ? 120 : undefined,
                              background: isEmpty ? '#f1f5f9' : `linear-gradient(135deg, ${s.from} 0%, ${s.to} 100%)`,
                              border: isEmpty ? '2px dashed #cbd5e1' : 'none',
                              boxShadow: isEmpty ? 'none' : `0 4px 16px ${s.from}50`,
                            }}
                          >
                            {isEmpty ? (
                              <span className="text-sm text-slate-400 font-medium">No deals</span>
                            ) : (
                              <span className="text-sm font-bold text-white drop-shadow-sm px-4 truncate">
                                {stage.count} deals · {formatCurrency(stage.value)}
                              </span>
                            )}
                          </div>

                          {/* Conversion % — shown OUTSIDE the bar on the right */}
                          {!isEmpty && i > 0 && stage.conversion_rate > 0 && (
                            <span className="text-xs font-semibold shrink-0 px-2 py-1 rounded-full whitespace-nowrap"
                              style={{ background: `${s.from}15`, color: s.from }}>
                              {stage.conversion_rate}% of pipeline
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-muted-foreground">
                  <p className="text-sm">No funnel data available for this period</p>
                </div>
              )}

              {/* Lost deals */}
              {funnel?.lost_count > 0 && (
                <div className="mt-5 flex items-center justify-between p-4 rounded-xl bg-red-50 border border-red-200">
                  <div className="flex items-center gap-2 text-red-700">
                    <div className="p-1.5 rounded-lg bg-red-100">
                      <XCircle className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Lost Deals: {funnel.lost_count}</p>
                      <p className="text-xs text-red-500">Closed lost in this period</p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-red-700 font-mono">{formatCurrency(funnel.lost_value)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rep Performance Tab */}
        <TabsContent value="reps" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Sales Rep Performance
                </div>
                <Badge variant="outline">{repPerformance?.total_reps || 0} reps</Badge>
              </CardTitle>
              <CardDescription>
                Top performer: <span className="font-bold text-primary">{repPerformance?.top_performer || 'N/A'}</span>
                {' • '}Average win rate: <span className="font-bold">{repPerformance?.avg_win_rate || 0}%</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {repPerformance?.reps?.map((rep, index) => (
                    <Card key={rep.name} className={index === 0 ? "border-2 border-amber-400 bg-amber-50" : ""}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                              index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-700' : 'bg-primary'
                            }`}>
                              {index < 3 ? ['🥇', '🥈', '🥉'][index] : index + 1}
                            </div>
                            <div>
                              <p className="font-medium">{rep.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {rep.total_opportunities} opportunities • {rep.won_count} won
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Won Value</p>
                              <p className="font-bold">{formatCurrency(rep.won_value)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Win Rate</p>
                              <Badge variant={rep.win_rate >= 70 ? "default" : rep.win_rate >= 50 ? "secondary" : "destructive"}>
                                {rep.win_rate}%
                              </Badge>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Avg Deal</p>
                              <p className="font-mono text-sm">{formatCurrency(rep.avg_deal_size)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Pipeline</p>
                              <p className="font-mono text-sm text-primary">{formatCurrency(rep.pipeline_value)}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Product Managers & Categories Tab */}
        <TabsContent value="teams" className="space-y-6">
          {/* Product Managers Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-violet-500" />
                Product Manager Performance
              </CardTitle>
              <CardDescription>{teamPerformance?.total_product_managers || 0} product managers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamPerformance?.product_managers?.slice(0, 6).map((pm, index) => (
                  <Card key={pm.name} className={index === 0 ? "border-2 border-violet-400 bg-violet-50/50" : "hover:shadow-md transition-shadow"}>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold ${
                            index === 0 ? 'bg-gradient-to-br from-violet-500 to-purple-600' : 
                            index === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-500' : 
                            index === 2 ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 
                            'bg-gradient-to-br from-gray-400 to-gray-500'
                          }`}>
                            {index < 3 ? ['🥇', '🥈', '🥉'][index] : (pm.name || 'U').charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{pm.name}</p>
                            <p className="text-xs text-muted-foreground">{pm.categories_count || 0} {(pm.categories_count || 0) === 1 ? 'category' : 'categories'}</p>
                          </div>
                        </div>
                        {index === 0 && <Badge className="bg-violet-500">Top PM</Badge>}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-lg font-bold text-emerald-600">{pm.won_count}</p>
                          <p className="text-[10px] text-muted-foreground">Won</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-sm font-bold">{formatCurrency(pm.won_value)}</p>
                          <p className="text-[10px] text-muted-foreground">Revenue</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2">
                          <Badge variant={pm.win_rate >= 70 ? "default" : pm.win_rate >= 50 ? "secondary" : "destructive"} className="text-xs">
                            {pm.win_rate}%
                          </Badge>
                          <p className="text-[10px] text-muted-foreground mt-1">Win Rate</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Solution Categories Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-500" />
                Solution Category Performance
              </CardTitle>
              <CardDescription>{teamPerformance?.total_categories || 0} {(teamPerformance?.total_categories || 0) === 1 ? 'solution category' : 'solution categories'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamPerformance?.categories?.slice(0, 6).map((cat, index) => (
                  <Card key={cat.name} className={index === 0 ? "border-2 border-blue-400 bg-blue-50/50" : "hover:shadow-md transition-shadow"}>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-xs ${
                            index === 0 ? 'bg-gradient-to-br from-blue-500 to-cyan-600' : 
                            index === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-500' : 
                            index === 2 ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 
                            'bg-gradient-to-br from-gray-400 to-gray-500'
                          }`}>
                            {index < 3 ? ['🥇', '🥈', '🥉'][index] : (cat.name || 'U').charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-sm truncate max-w-[150px]">{cat.name}</p>
                            <p className="text-xs text-muted-foreground">{cat.product_managers_count || 0} PMs</p>
                          </div>
                        </div>
                        {index === 0 && <Badge className="bg-blue-500">Top Category</Badge>}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-lg font-bold text-emerald-600">{cat.won_count}</p>
                          <p className="text-[10px] text-muted-foreground">Won</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-sm font-bold">{formatCurrency(cat.won_value)}</p>
                          <p className="text-[10px] text-muted-foreground">Revenue</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2">
                          <Badge variant={cat.win_rate >= 70 ? "default" : cat.win_rate >= 50 ? "secondary" : "destructive"} className="text-xs">
                            {cat.win_rate}%
                          </Badge>
                          <p className="text-[10px] text-muted-foreground mt-1">Win Rate</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Account Health Tab */}
        <TabsContent value="accounts" className="space-y-4">
          {/* Account Summary Cards - Now Clickable */}
          <div className="grid grid-cols-3 gap-4">
            <Card 
              className="bg-emerald-50 border-emerald-200 cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => { setRiskModalType('healthy'); setRiskModalOpen(true); }}
            >
              <CardContent className="pt-6 text-center">
                <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-3xl font-bold text-emerald-700">{accountHealth?.summary?.healthy || 0}</p>
                <p className="text-sm text-emerald-600">Healthy Accounts</p>
                <p className="text-xs text-emerald-500 mt-2 flex items-center justify-center gap-1">
                  Click to view <ChevronRight className="h-3 w-3" />
                </p>
              </CardContent>
            </Card>
            <Card 
              className="bg-amber-50 border-amber-200 cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => { setRiskModalType('at_risk'); setRiskModalOpen(true); }}
            >
              <CardContent className="pt-6 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto text-amber-600 mb-2" />
                <p className="text-3xl font-bold text-amber-700">{accountHealth?.summary?.at_risk || 0}</p>
                <p className="text-sm text-amber-600">At Risk</p>
                <p className="text-xs text-amber-500 mt-2 flex items-center justify-center gap-1">
                  Click to view <ChevronRight className="h-3 w-3" />
                </p>
              </CardContent>
            </Card>
            <Card 
              className="bg-red-50 border-red-200 cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => { setRiskModalType('dormant'); setRiskModalOpen(true); }}
            >
              <CardContent className="pt-6 text-center">
                <Clock className="h-8 w-8 mx-auto text-red-600 mb-2" />
                <p className="text-3xl font-bold text-red-700">{accountHealth?.summary?.dormant || 0}</p>
                <p className="text-sm text-red-600">Dormant</p>
                <p className="text-xs text-red-500 mt-2 flex items-center justify-center gap-1">
                  Click to view <ChevronRight className="h-3 w-3" />
                </p>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Account Engagement Scores</CardTitle>
              <CardDescription>Top 50 accounts by engagement</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {accountHealth?.accounts?.map((account) => (
                    <div key={account.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          account.status === 'healthy' ? 'bg-emerald-500' :
                          account.status === 'at_risk' ? 'bg-amber-500' : 'bg-red-500'
                        }`} />
                        <div>
                          <p className="font-medium">{account.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {account.total_opportunities} opportunities • {account.active_opportunities} active
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-mono">{formatCurrency(account.total_value)}</p>
                          <p className="text-xs text-muted-foreground">Pipeline</p>
                        </div>
                        <div className="w-24">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs">Score</span>
                            <span className="text-xs font-bold">{account.engagement_score}</span>
                          </div>
                          <Progress 
                            value={account.engagement_score} 
                            className="h-2"
                          />
                        </div>
                        <Badge variant={
                          account.status === 'healthy' ? 'default' :
                          account.status === 'at_risk' ? 'secondary' : 'destructive'
                        }>
                          {account.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <Card className="rounded-2xl border-slate-200 shadow-sm overflow-hidden">
          <CardHeader className="border-b border-slate-100 bg-slate-50">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-[#800000]" /> Top Overdue Customers
            </CardTitle>
            <CardDescription>Customer-level spotlight from the current invoice collection scope.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {(invoiceHealth?.top_overdue || []).slice(0, 6).map((item, index) => (
                <div key={`${item.invoice_number}-${index}`} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{item.account_name || 'Unknown Customer'}</p>
                    <p className="text-[11px] text-slate-500">{item.invoice_number || 'Invoice'} • {item.salesperson || 'Unassigned'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-red-600">{money(item.amount || 0)}</p>
                    <p className="text-[11px] text-slate-500">{item.days_overdue || 0}d overdue</p>
                  </div>
                </div>
              ))}
              {(!invoiceHealth?.top_overdue || invoiceHealth.top_overdue.length === 0) && (
                <div className="px-5 py-10 text-center text-sm text-slate-400">No overdue customer rows in the current scope</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* AI Insights Tab — Rich formatted output */}
        <TabsContent value="ai" className="space-y-4">
          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
            {/* Header bar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#800000]/10">
                  <Brain className="h-5 w-5 text-[#800000]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">AI-Generated Sales Intelligence</h3>
                  <p className="text-slate-500 text-xs">Powered by {aiInsights?.ai_model || 'GPT-5.2'}</p>
                </div>
              </div>
              <button
                onClick={loadAIInsights}
                disabled={aiLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={{ background: '#ffffff', color: '#0f172a', border: '1px solid #e2e8f0' }}
                data-testid="generate-ai-insights"
              >
                {aiLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {aiLoading ? 'Generating...' : 'Refresh'}
              </button>
            </div>

            {/* Data summary stats */}
            {aiInsights?.data_summary && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-b border-slate-100">
                {[
                  { label: 'Opportunities', value: aiInsights.data_summary.total_opportunities, color: '#7c3aed' },
                  { label: 'Pipeline Value', value: formatCurrency(aiInsights.data_summary.total_pipeline_value), color: '#2563eb' },
                  { label: 'Win Rate', value: `${aiInsights.data_summary.win_rate}%`, color: '#059669' },
                  { label: 'Avg Deal Size', value: formatCurrency(aiInsights.data_summary.avg_deal_size), color: '#d97706' },
                ].map((stat, i) => (
                  <div key={stat.label} className={`px-5 py-3.5 ${i < 3 ? 'lg:border-r border-slate-100' : ''}`}>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">{stat.label}</p>
                    <p className="text-xl font-black tracking-tight" style={{ color: stat.color }}>{stat.value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Main insights content */}
            <div className="px-6 py-5">
              {aiLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="relative w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7c3aed20, #4f46e520)' }}>
                    <Brain className="h-8 w-8 text-violet-500 animate-pulse" />
                  </div>
                  <p className="text-sm font-medium text-slate-600">Analyzing your sales data with AI...</p>
                  <div className="flex gap-1">
                    {[0,200,400].map(d => <span key={d} className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                  </div>
                </div>
              ) : aiInsights?.insights ? (
                <AIInsightsRenderer text={aiInsights.insights} formatCurrency={formatCurrency} />
              ) : (
                <div className="text-center py-10">
                  <Brain className="h-12 w-12 mx-auto text-violet-300 mb-3" />
                  <p className="font-semibold text-slate-700 mb-1">AI Insights on demand</p>
                  <p className="text-sm text-slate-400 mb-4">Click "Generate" above to analyse your sales data with AI</p>
                  <button
                    onClick={loadAIInsights}
                    disabled={aiLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all"
                    style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', color: 'white', boxShadow: '0 4px 14px rgba(124,58,237,0.35)' }}
                    data-testid="generate-ai-insights-empty"
                  >
                    <Sparkles className="h-4 w-4" /> Generate Insights
                  </button>
                </div>
              )}
            </div>

            {aiInsights?.generated_at && (
              <div className="px-6 pb-4 text-[10px] text-slate-400 flex items-center gap-1">
                <span>Last generated:</span>
                <span className="font-medium">{new Date(aiInsights.generated_at).toLocaleString()}</span>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── AI Insights Rich Renderer ──────────────────────────────────────────────────
// Parses the AI text and renders it with proper formatting: sections, tables, bullets
function AIInsightsRenderer({ text, formatCurrency }) {
  if (!text) return null;

  // Parse the AI markdown-like text into structured sections
  const lines = text.split('\n').filter(Boolean);
  const sections = [];
  let currentSection = null;
  let currentItems = [];

  const flushSection = () => {
    if (currentSection) {
      sections.push({ ...currentSection, items: currentItems });
      currentSection = null;
      currentItems = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Section header (##, ###, **Title**, or ALL CAPS lines)
    if (trimmed.startsWith('## ') || trimmed.startsWith('### ') || /^\*\*[A-Z]/.test(trimmed)) {
      flushSection();
      const title = trimmed.replace(/^#+\s*/, '').replace(/\*\*/g, '').replace(/:$/, '');
      currentSection = { title, type: 'section' };
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.startsWith('* ')) {
      const item = trimmed.replace(/^[-•*]\s+/, '').replace(/\*\*/g, '');
      currentItems.push({ type: 'bullet', text: item });
    } else if (/^\d+\./.test(trimmed)) {
      const item = trimmed.replace(/^\d+\.\s+/, '').replace(/\*\*/g, '');
      currentItems.push({ type: 'numbered', text: item });
    } else if (trimmed.includes('|') && trimmed.split('|').length >= 3) {
      // Table row
      if (!currentSection) currentSection = { title: 'Data', type: 'section' };
      const cells = trimmed.split('|').map(c => c.trim()).filter(Boolean);
      if (!cells.every(c => /^[-:]+$/.test(c))) {
        currentItems.push({ type: 'table_row', cells });
      }
    } else {
      if (!currentSection) currentSection = { title: '', type: 'section' };
      currentItems.push({ type: 'paragraph', text: trimmed.replace(/\*\*/g, '') });
    }
  }
  flushSection();

  const sectionIcons = ['📊', '🎯', '⚠️', '💡', '📈', '🔑', '✅', '🚀'];
  const sectionColors = [
    { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
    { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
    { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
    { bg: '#fdf4ff', border: '#e9d5ff', text: '#7c3aed' },
    { bg: '#f0f9ff', border: '#bae6fd', text: '#0369a1' },
  ];

  return (
    <div className="space-y-4" data-testid="ai-insights-renderer">
      {sections.map((section, si) => {
        const c = sectionColors[si % sectionColors.length];
        const icon = sectionIcons[si % sectionIcons.length];
        const tableRows = section.items.filter(i => i.type === 'table_row');
        const nonTableItems = section.items.filter(i => i.type !== 'table_row');

        return (
          <div key={si} className="rounded-xl overflow-hidden border" style={{ borderColor: c.border }}>
            {/* Section header */}
            {section.title && (
              <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
                <span className="text-base">{icon}</span>
                <h4 className="font-bold text-sm" style={{ color: c.text }}>{section.title}</h4>
              </div>
            )}
            {/* Content */}
            <div className="px-4 py-3 bg-white/70 space-y-2">
              {/* Paragraphs */}
              {nonTableItems.filter(i => i.type === 'paragraph').map((item, ii) => (
                <p key={ii} className="text-sm text-slate-700 leading-relaxed">{item.text}</p>
              ))}
              {/* Bullets/Numbered */}
              {nonTableItems.filter(i => i.type === 'bullet' || i.type === 'numbered').length > 0 && (
                <ul className="space-y-1.5">
                  {nonTableItems.filter(i => i.type === 'bullet' || i.type === 'numbered').map((item, ii) => (
                    <li key={ii} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold" style={{ background: c.bg, color: c.text }}>
                        {item.type === 'numbered' ? ii+1 : '•'}
                      </span>
                      <span className="leading-relaxed">{item.text}</span>
                    </li>
                  ))}
                </ul>
              )}
              {/* Table */}
              {tableRows.length > 1 && (
                <div className="rounded-lg overflow-hidden border border-slate-100 mt-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: c.bg }}>
                        {tableRows[0].cells.map((cell, ci) => (
                          <th key={ci} className="px-3 py-2 text-left font-semibold" style={{ color: c.text }}>{cell}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.slice(1).map((row, ri) => (
                        <tr key={ri} className="border-t border-slate-100 hover:bg-slate-50/50">
                          {row.cells.map((cell, ci) => (
                            <td key={ci} className="px-3 py-2 text-slate-700">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
